import React, { memo } from 'react'

import BaseCard from '../shared/baseCard/BaseCard'
import Text from '../shared/text/Text'
import DefaultText from '../shared/text/DefaultText'

const PrivacyPolicy = ({ t }) => {
  return (
    <BaseCard>
      <Text variant='h2' label={t('home.privacy-policy')} fontWeight={700} />
      <DefaultText
        label={
          <>
            <p>
              <strong>Effective Date:</strong> 10 September 2024
            </p>

            <p>
              At the qiibee Foundation, we are committed to protecting and
              respecting your privacy. This Privacy Policy outlines how we
              collect, use, store, and protect any data when you visit our
              website and participate in the QBX token sale.
            </p>

            <p>
              We do not collect personal information such as your name, email
              address, or other contact details. By using our website and
              participating in the token sale, you agree to the terms outlined
              in this Privacy Policy.
            </p>

            <h3>1. Information We Collect</h3>

            <p>
              We collect minimal information related to your interaction with
              the website and blockchain transactions:
            </p>

            <ul>
              <li>
                <strong>Transaction Information:</strong> We collect details
                about the transactions you make, including the amount of ETH
                sent, transaction ID, and Ethereum wallet addresses. This
                information is publicly recorded on the blockchain. We also map
                the amount of Deposit Points you receive as part of your
                contributions.
              </li>
              <li>
                <strong>Log Data:</strong> Our servers may automatically collect
                information about your visit, such as your IP address, browser
                type, and page interactions. This is used to monitor and improve
                the website&apos;s functionality.
              </li>
              <li>
                <strong>Cookies:</strong> We may use cookies to improve user
                experience. These are small files stored on your device to track
                preferences and usage.
              </li>
              <li>
                <strong>Referrals:</strong> If you refer users or are referred
                by another user or community we will be tracking it in order to
                reward the referrer.
              </li>
              <li>
                <strong>Analytics Data:</strong> We use third-party services
                such as Google Analytics to collect anonymized information about
                how visitors use our website. This includes data such as your IP
                address, browser type, pages visited, time spent on those pages,
                and other anonymous usage statistics. Google Analytics uses
                cookies to collect this information.
              </li>
            </ul>

            <h3>2. How We Use Your Information</h3>

            <p>We use the information we collect for the following purposes:</p>

            <ul>
              <li>
                <strong>Transaction Processing:</strong> To monitor and verify
                blockchain transactions related to the token sale.
              </li>
              <li>
                <strong>Improvement of Services:</strong> To analyze usage
                patterns and improve our website and token sale operations.
              </li>
              <li>
                <strong>Rewards:</strong> To reward users with Deposit Points
                based on activity history and referrals.
              </li>
              <li>
                <strong>Website Analytics:</strong> We use Google Analytics to
                understand how visitors interact with our website, improve user
                experience, and monitor the performance of our token sale.
              </li>
            </ul>

            <h3>3. Sharing Your Information</h3>

            <p>
              We do not sell or rent your personal information to third parties.
              However, some information may be shared in the following
              situations:
            </p>

            <ul>
              <li>
                <strong>Service Providers:</strong> We share data with
                third-party service providers (in particular qiibee ag,
                Alpenstrasse 7, 6300 Zug, Switzerland - &quot;qiibee ag&quot;)
                that assist in maintaining our website or providing analytics
                services (such as Google Analytics).
              </li>
              <li>
                <strong>Legal Requirements:</strong> If required by law, we may
                disclose minimal information (such as IP addresses or blockchain
                transaction data) to comply with legal obligations.
              </li>
              <li>
                <strong>Protection of Rights:</strong> We may disclose
                information to enforce our terms and conditions, protect our
                operations, or prevent fraud or security threats.
              </li>
            </ul>

            <h3>4. Cookies and Tracking Technologies</h3>

            <p>
              We use cookies and similar tracking technologies to enhance your
              experience on our website. Cookies are small data files stored on
              your device that help us track usage patterns, improve website
              performance, and ensure the website works efficiently.
            </p>

            <ul>
              <li>
                <strong>Google Analytics:</strong> We use Google Analytics, a
                web analytics service provided by Google, Inc. Google Analytics
                uses cookies to help us analyze how users interact with the
                website. The information generated by the cookie about your use
                of the website will be transmitted to and stored by Google on
                servers in the United States.
              </li>
            </ul>

            <p>
              Google Analytics may collect anonymized information such as your
              IP address, browser type, and behavior on our website. This data
              helps us understand user trends and improve the services we
              provide.
            </p>

            <p>
              You can opt-out of Google Analytics by installing the{' '}
              <a
                href='https://tools.google.com/dlpage/gaoptout'
                style={{ color: 'inherit' }}
                rel='noreferrer'
                target='_blank'
              >
                Google Analytics Opt-out Browser Add-on
              </a>
              . For more information on how Google collects and processes data,
              please visit{' '}
              <a
                href='https://policies.google.com/privacy'
                style={{ color: 'inherit' }}
                target='_blank'
                rel='noreferrer'
              >
                Google&apos;s Privacy & Terms
              </a>
              .
            </p>

            <h3>5. Data Security</h3>

            <p>
              We implement reasonable administrative, technical, and physical
              measures to protect your information from unauthorized access,
              disclosure, or misuse. However, no system is entirely secure, and
              we cannot guarantee the absolute security of your information.
            </p>

            <h3>6. Your Rights</h3>

            <p>
              You have the following rights concerning your interaction with our
              website:
            </p>

            <ul>
              <li>
                <strong>Access:</strong> As all transaction data is stored on
                the blockchain, you can view your transaction details via the
                Ethereum blockchain at any time. You view the mapped amount of
                deposit points and number of tickets you&apos;ve received in the
                dashboard.
              </li>
              <li>
                <strong>Opt-Out of Analytics Tracking:</strong> You have the
                right to opt-out of having your data used by Google Analytics by
                adjusting your cookie settings or using the Google Analytics
                Opt-out Browser Add-on.
              </li>
            </ul>

            <h3>7. Retention of Data</h3>

            <p>
              We will retain transaction and analytics data only as long as
              necessary to fulfill the purposes outlined in this Privacy Policy
              or to comply with legal obligations.
            </p>

            <h3>8. Third-Party Links</h3>

            <p>
              Our website may contain links to third-party websites or services.
              We are not responsible for the privacy practices or content of
              those third-party websites.
            </p>

            <h3>9. Children&apos;s Privacy</h3>

            <p>
              Our services are not intended for use by individuals under the age
              of 18, and we do not knowingly collect personal information from
              children.
            </p>

            <h3>10. Changes to this Privacy Policy</h3>

            <p>
              We may update this Privacy Policy from time to time. Any changes
              will be posted on this page, with the &quot;Effective Date&quot;
              updated accordingly. We encourage you to review this policy
              periodically.
            </p>

            <h3>11. Contact Us</h3>

            <p>
              If you have any questions or concerns about this Privacy Policy or
              how we handle your data, please contact us at:
            </p>

            <p>
              <strong>qiibee foundation</strong>
            </p>

            <p>
              <a
                href='mailto:foundationtcpp@qiibee.com'
                style={{ color: 'inherit' }}
              >
                foundationtcpp@qiibee.com
              </a>
            </p>

            <p>Alpenstrasse 7, 6300 Zug, Switzerland</p>
          </>
        }
      />
    </BaseCard>
  )
}

export default memo(PrivacyPolicy)
